import { QueryKey } from '@tanstack/query-core';

import { GenericFailure, OffsetLimit } from '@playq/services-shared';
import {
  Attachment,
  AttachmentSearchQuerySerialized,
  IssueId,
  IssueSort,
  MetaKeyWithCounter,
  RequestStatus,
  SupportRequestId,
  SupportRequestSort,
  SupportRequestSortField,
} from '@playq/services-beetle';
import { Environment } from '@playq/octopus-common';
import { BeetleIssue, BeetleSupportRequest } from '@playq/octopus2-apps';

import { ICollectionTableQueryChange } from '/shared/Table';
import {
  IBeetleId,
  IBeetleParams,
  IIssueOrRequestSort,
  IIssueOrSupportBrief,
  IUpdateBatchData,
  IUpdateData,
} from '/component/Support/BeetlesLookup/types';
import { IQuery, IWithReadonly } from '/common/models';
import { ModalProps } from '/hooks/useModal';

import { IUseExportBeetleToJiraDialogProps } from '../ExportBeetleToJiraDialog';

export type SupportRequestsQuery = IQuery<SupportRequestSortField, SupportRequestSort>;

export type IssuesQuery = IQuery<string, IssueSort>;
export type AttachmentQuery = IQuery<string>;

export interface BeetleTableProps<QueryType> {
  initialQuery: QueryType;
  metaKeys: MetaKeyWithCounter[];
  onQueryChange: (qc: ICollectionTableQueryChange<QueryType>) => void;
  attachment?: boolean;
  attachmentsQuery?: Partial<AttachmentSearchQuerySerialized>;
}
export interface IAttachmentsTableProps extends Omit<
  BeetleTableProps<IQuery<string>>,
  'readonly' | 'metaKeys' | 'attachment' | 'attachmentsQuery'
> {
  queryAttachments: {
    total: number;
    attachments: Attachment[];
    refetch: () => void;
    dataUpdatedAt: number;
    isLoading: boolean;
  };
}

export interface TQueryCacheProps {
  mutateUpdated: (updateData: IUpdateData) => void;
  mutateUpdatedBatchStatus: (updateData: IUpdateBatchData) => void;
  mutateDeletedEntity: (entityId: IBeetleId) => void;
  removeItemBatch: (ids: IBeetleId[], updatedIterator: OffsetLimit) => void;
}

export interface QueryEntities<R> extends TQueryCacheProps {
  entities: R[];
  isFetching: boolean;
  total: number;
  refetch: () => void;
  queryKeys: QueryKey;
  error: Error | GenericFailure | null;
}

export type ITableProps = {
  queryEntites: QueryEntities<IIssueOrSupportBrief>;
  selectedItems: IIssueOrSupportBrief[];
  setSelectedItems: (selectedItems: IIssueOrSupportBrief[]) => void;
} & BeetleTableProps<PersistQuery<IIssueOrRequestSort>>;

export type CommonQuery = IssuesQuery | SupportRequestsQuery;
export type PersistQuery<S> = IQuery<string, S>;

export type Sort = IssueSort | SupportRequestSort;

export interface ISharedProps {
  params: Pick<IBeetleParams, 'env' | 'type'>;
  queryKeys: QueryKey;
  currentKeys: QueryKey;
}
export interface TTrashButtonProps extends ISharedProps {
  selectedItems: IIssueOrSupportBrief[];
  removeItemBatch: TQueryCacheProps['removeItemBatch'];
  iterator: OffsetLimit;
}

export enum UpdateRequestType {
  Accepted = 'Accepted',
  Declined = 'Declined',
  BeingProcessed = 'BeingProcessed',
  Resolved = 'Resolved',
  Sent = 'Sent',
}

export interface IUpdateBatchButtonProps {
  renderModal: (modal: ModalProps, handleStatusBatch: (mark: boolean | RequestStatus) => void) => void;
  support?: boolean;
  selectedItems: IIssueOrSupportBrief[];
  mutateUpdatedBatchStatus: TQueryCacheProps['mutateUpdatedBatchStatus'];
}
export interface SelectedStatusListProps {
  modal: Omit<ModalProps, 'open' | 'close' | 'isOpen'>;
  updateRequest: (mark: boolean | RequestStatus) => void;
}

export interface IBeetleButtonGroup {
  beetleId: IssueId | SupportRequestId;
}
export interface ButtonProps extends IBeetleButtonGroup, IWithReadonly {
  params: IBeetleParams;
}
export interface TEntityArg extends IBeetleButtonGroup {
  EntityClass: new () => BeetleIssue | BeetleSupportRequest;
  env: Environment;
}
export interface TExportJiraArg extends IBeetleButtonGroup {
  params: Pick<IBeetleParams, 'env' | 'type'>;
  location: Location;
  openDialog: (obj: IUseExportBeetleToJiraDialogProps) => void;
}
